// Fichier: src/pages/accounting/IncomeStatement.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableRow, TableCell,
  TableContainer, IconButton, TextField, MenuItem, Chip, Grid, Divider
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

// ── Rubriques PCMN (préfixe à 2 chiffres du code de compte) ────────────
const CHARGE_LABELS = {
  60: 'Achats',
  61: 'Services et biens divers',
  62: 'Rémunérations, charges sociales et pensions',
  63: 'Amortissements et provisions',
  64: "Autres charges d'exploitation",
  65: 'Charges financières',
  66: 'Charges exceptionnelles'
};

const PRODUIT_LABELS = {
  70: 'Cotisations, dons, legs et subsides',
  73: 'Prestations de services',
  74: "Autres produits d'exploitation",
  75: 'Produits financiers',
  76: 'Produits exceptionnels'
};

function groupLabel(account) {
  const prefix = (account.code || '').slice(0, 2);
  if (account.classNumber === 6) return CHARGE_LABELS[prefix] || "Autres charges d'exploitation";
  if (account.classNumber === 7) return PRODUIT_LABELS[prefix] || "Autres produits d'exploitation";
  return null;
}

const IncomeStatement = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    const unsubAccounts = onSnapshot(collection(db, 'accounting_accounts'), (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubEntries = onSnapshot(
      query(collection(db, 'accounting_entries'), where('status', '==', 'validé')),
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => {
      unsubAccounts();
      unsubEntries();
    };
  }, []);

  const years = useMemo(() => {
    const set = new Set(entries.map((e) => e.exerciceId).filter(Boolean));
    set.add(String(new Date().getFullYear()));
    return Array.from(set).sort().reverse();
  }, [entries]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.id] = a; });
    return map;
  }, [accounts]);

  const { chargeGroups, produitGroups, totalCharges, totalProduits } = useMemo(() => {
    const balances = {}; // accountId -> montant net (toujours positif : charge ou produit)

    entries
      .filter((e) => e.exerciceId === year)
      .forEach((e) => {
        (e.lines || []).forEach((l) => {
          const account = accountsById[l.accountId];
          if (!account || (account.classNumber !== 6 && account.classNumber !== 7)) return;
          const debit = Number(l.debit) || 0;
          const credit = Number(l.credit) || 0;
          if (!balances[account.id]) balances[account.id] = 0;
          balances[account.id] += account.classNumber === 6 ? debit - credit : credit - debit;
        });
      });

    const buildGroups = (classNumber) => {
      const groupsMap = {};
      accounts
        .filter((a) => a.classNumber === classNumber)
        .forEach((a) => {
          const solde = balances[a.id] || 0;
          if (Math.abs(solde) < 0.005) return;
          const label = groupLabel(a);
          if (!groupsMap[label]) groupsMap[label] = { label, accounts: [], total: 0 };
          groupsMap[label].accounts.push({ ...a, solde });
          groupsMap[label].total += solde;
        });
      return Object.values(groupsMap).sort((a, b) => a.label.localeCompare(b.label));
    };

    const charges = buildGroups(6);
    const produits = buildGroups(7);
    const totalC = charges.reduce((s, g) => s + g.total, 0);
    const totalP = produits.reduce((s, g) => s + g.total, 0);

    return { chargeGroups: charges, produitGroups: produits, totalCharges: totalC, totalProduits: totalP };
  }, [accounts, entries, accountsById, year]);

  const resultat = totalProduits - totalCharges;

  const renderSection = (title, groups, total) => (
    <TableContainer component={Paper} sx={{ height: '100%' }}>
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{title}</Typography>
      </Box>
      <Table size="small">
        <TableBody>
          {groups.map((g) => (
            <React.Fragment key={g.label}>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={2}><strong>{g.label}</strong></TableCell>
              </TableRow>
              {g.accounts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell sx={{ pl: 4 }}>
                    <span style={{ fontFamily: 'monospace', marginRight: 8 }}>{a.code}</span>
                    {a.label}
                  </TableCell>
                  <TableCell align="right">{a.solde.toFixed(2)} €</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ pl: 4 }}><em>Sous-total {g.label.toLowerCase()}</em></TableCell>
                <TableCell align="right"><em>{g.total.toFixed(2)} €</em></TableCell>
              </TableRow>
            </React.Fragment>
          ))}
          {groups.length === 0 && (
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                Aucune donnée pour cet exercice.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: 'grey.100' }}>
        <Typography sx={{ fontWeight: 'bold' }}>TOTAL {title.toUpperCase()}</Typography>
        <Typography sx={{ fontWeight: 'bold' }}>{total.toFixed(2)} €</Typography>
      </Box>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Compte de résultats</Typography>
        </Box>
        <TextField select size="small" label="Exercice" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {years.map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </Box>

      {!loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderSection('Charges', chargeGroups, totalCharges)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSection('Produits', produitGroups, totalProduits)}
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 3, mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Résultat de l'exercice {year}</Typography>
        <Chip
          label={`${resultat >= 0 ? '+' : ''}${resultat.toFixed(2)} € ${resultat >= 0 ? '(bénéfice)' : '(perte)'}`}
          color={resultat >= 0 ? 'success' : 'error'}
          sx={{ fontSize: '1rem', py: 2 }}
        />
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Ce résultat doit correspondre à la ligne "Résultat de l'exercice {year}" dans le bilan pour ce même exercice.
      </Typography>
    </Container>
  );
};

export default IncomeStatement;
